import React, { memo, useState, useEffect, useCallback, useRef } from 'react'
import { makeStyles, useTheme } from '@mui/styles'
import { CSSTransition } from 'react-transition-group'
import ThemeInterface from '@/theme-interface'
import { TimeoutProps } from 'react-transition-group/Transition'

// styles are used but eslint plugin doesn't recognize it
/* eslint-disable mui-unused-classes/unused-classes */
const useStyles = makeStyles((theme: ThemeInterface) => ({
  enterActive: ({ color }: { color: string }) => ({
    backgroundColor: `${color} !important`,
    borderRadius: theme.borderRadius,
    // exclude the react-select menu popup ([class*="menu"]) so the highlight only
    // affects the input/control, not the dropdown that opens as a sibling child.
    // The wrapper div adds a level above react-select's own (transparent) container,
    // so the control is also targeted directly via its stable class name.
    '& input, & > *:not([class*="menu"]), & .react-select__control': {
      backgroundColor: `${color} !important`,
      borderRadius: theme.borderRadius,
    },
    transition: 'background-color 1s',
  }),
  exit: {
    backgroundColor: 'inherit',
    borderRadius: theme.borderRadius,
    '& input, & > *:not([class*="menu"]), & .react-select__control': {
      backgroundColor: 'inherit',
      borderRadius: theme.borderRadius,
    },
  },
  exitActive: {
    backgroundColor: 'inherit',
    borderRadius: theme.borderRadius,
    '& input, & > *:not([class*="menu"]), & .react-select__control': {
      backgroundColor: 'inherit',
      borderRadius: theme.borderRadius,
    },
    transition: 'background-color 1s',
  },
  // Layout-transparent (display: contents) so wrapping doesn't disturb the parent's
  // flex/grid sizing of the real child (e.g. select-input.css's `.selectContainer`
  // is `display: flex` and react-select's own container relies on `flexGrow: 1`).
  wrapper: {
    display: 'contents',
  },
}))

export type FillTransitionProps = Omit<TimeoutProps<undefined>, 'timeout'> & {
  show?: boolean
  color?: string
  timeout?: number | { appear?: number; enter?: number; exit?: number }
  classNames?: string | {
    appear?: string
    appearActive?: string
    appearDone?: string
    enter?: string
    enterActive?: string
    enterDone?: string
    exit?: string
    exitActive?: string
    exitDone?: string
  }
}

const FillTransition = memo((props: FillTransitionProps) => {
  const theme = useTheme<ThemeInterface>()
  const { show: propsShow = false, color = theme.primaryLight, children, ...rest } = props
  const classes = useStyles({ color })

  const [show, setShow] = useState<boolean>(false)
  useEffect(() => { setShow(propsShow) }, [propsShow])

  const onEnteredHandle = useCallback(() => {
    setShow(false)
  }, [])

  // A React-owned nodeRef, not react-transition-group's deprecated findDOMNode
  // fallback: findDOMNode can resolve to a node that a surrounding unmount (e.g.
  // toggling Bcc, or a select-variant swap in SelectInput) already detached while
  // a transition was still in flight, causing a "removeChild: not a child" crash.
  const nodeRef = useRef<HTMLDivElement>(null)

  return (
    <CSSTransition
      nodeRef={nodeRef}
      in={show}
      timeout={2000}
      classNames={classes}
      onEntered={onEnteredHandle}
      {...rest}>
      <div ref={nodeRef} className={classes.wrapper}>{children}</div>
    </CSSTransition>
  )
})

export default FillTransition
